import React, { useState, useEffect, useCallback } from 'react';
import { API_ENDPOINTS } from '../utils/apiConfig';

/**
 * Widget affichant le suivi de l'annualisation des heures
 * Pour les utilisateurs non-cadres avec politique annualisee
 */
const AnnualizationWidget = ({ token, compact = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const loadData = useCallback(async () => {
    if (!token) return;

    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/leave-balances/hours/summary`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();

      if (result.success) {
        setData(result.data);
      } else {
        setError(result.message || 'Erreur chargement');
      }
    } catch (err) {
      setError('Erreur connexion serveur');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Synchroniser les heures
  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/leave-balances/hours/sync`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const result = await response.json();

      if (result.success) {
        loadData(); // Recharger les donnees
      }
    } catch (err) {
      console.error('Erreur sync:', err);
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
      }}>
        <div style={{ textAlign: 'center', color: '#666', fontSize: '14px' }}>
          Chargement...
        </div>
      </div>
    );
  }

  // Pas de suivi horaire pour cet utilisateur (cadre ou pas de politique annualisee)
  if (!data) {
    return null; // Ne rien afficher
  }

  if (error && !data) {
    return (
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
      }}>
        <div style={{ textAlign: 'center', color: '#dc3545', fontSize: '14px' }}>
          {error}
        </div>
      </div>
    );
  }

  // Calcul du delta et de la couleur
  const delta = parseFloat(data.delta_ytd || 0);
  const progress = parseFloat(data.progress_percent || 0);
  const hoursTotal = parseFloat(data.hours_total || 0);
  const annualTarget = parseFloat(data.annual_target || 1607);
  const expectedYTD = parseFloat(data.hours_expected_ytd || 0);

  const getProgressColor = () => {
    // En avance ou à jour (dans les -20h) : vert, peu importe l'ampleur de l'avance
    if (delta >= -20) return '#28a745';
    // Retard significatif mais rattrapable
    if (delta > -50) return '#ffc107';
    // Retard fort
    return '#dc3545';
  };

  // Version compacte
  if (compact) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        background: '#f8f9fa',
        padding: '6px 12px',
        borderRadius: '20px',
        fontSize: '13px'
      }}>
        <span>H</span>
        <strong>{hoursTotal.toFixed(0)}h</strong>
        <span style={{ color: '#666' }}>/ {annualTarget}h</span>
      </div>
    );
  }

  // Version complete
  return (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          padding: '16px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          background: expanded ? '#f8f9fa' : 'white',
          transition: 'background 0.2s'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '22px' }}>Horloge</span>
          <span style={{ fontWeight: '600', fontSize: '16px', color: '#333' }}>
            Mes heures
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Quick summary */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{
              fontWeight: '700',
              fontSize: '16px',
              color: '#333'
            }}>
              {hoursTotal.toFixed(0)}h
            </span>
            <span style={{ color: '#666', fontSize: '13px' }}>
              / {annualTarget}h
            </span>
            <span style={{
              background: getProgressColor(),
              color: 'white',
              padding: '3px 8px',
              borderRadius: '10px',
              fontSize: '11px',
              fontWeight: '600'
            }}>
              {progress.toFixed(0)}%
            </span>
          </div>
          <span style={{
            fontSize: '12px',
            color: '#666',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0)',
            transition: 'transform 0.2s'
          }}>
            ▼
          </span>
        </div>
      </div>

      {/* Details (expandable) */}
      {expanded && (
        <div style={{ padding: '0 20px 20px' }}>
          {/* Progress bar */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{
              background: '#e9ecef',
              borderRadius: '10px',
              height: '20px',
              overflow: 'hidden',
              position: 'relative'
            }}>
              {/* Barre de progression reelle */}
              <div style={{
                background: getProgressColor(),
                height: '100%',
                width: `${Math.min(100, progress)}%`,
                transition: 'width 0.3s ease',
                borderRadius: '10px'
              }} />
              {/* Marqueur theorique */}
              <div style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: `${Math.min(100, (expectedYTD / annualTarget) * 100)}%`,
                width: '3px',
                background: '#333',
                opacity: 0.5
              }} />
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: '6px',
              fontSize: '11px',
              color: '#666'
            }}>
              <span>0h</span>
              <span>{annualTarget}h</span>
            </div>
          </div>

          {/* Details cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
            <div style={{
              background: '#f8f9fa',
              padding: '12px',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>
                Heures travaillees
              </div>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#333' }}>
                {parseFloat(data.hours_worked || 0).toFixed(0)}h
              </div>
            </div>

            <div style={{
              background: '#f8f9fa',
              padding: '12px',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>
                Heures deplacement
              </div>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#333' }}>
                {parseFloat(data.hours_travel || 0).toFixed(0)}h
              </div>
            </div>

            <div style={{
              background: '#f8f9fa',
              padding: '12px',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>
                Theorique a date
              </div>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#666' }}>
                {expectedYTD.toFixed(0)}h
              </div>
            </div>
          </div>

          {/* Heures route (cumul) */}
          {parseFloat(data.travel_hours_total || 0) > 0 && (
            <div style={{
              marginTop: '12px',
              padding: '10px 12px',
              background: '#e8eaf6',
              borderRadius: '8px',
              fontSize: '12px',
              color: '#283593',
              textAlign: 'center'
            }}>
              Heures route (cumul): <strong>{parseFloat(data.travel_hours_total).toFixed(0)}h</strong>
            </div>
          )}

          {/* Heures week-end */}
          {parseFloat(data.weekend_hours || 0) > 0 && (
            <div style={{
              marginTop: '8px',
              padding: '10px 12px',
              background: '#fce4ec',
              borderRadius: '8px',
              fontSize: '12px',
              color: '#880e4f',
              textAlign: 'center'
            }}>
              Heures week-end: <strong>{parseFloat(data.weekend_hours).toFixed(0)}h</strong>
            </div>
          )}

          {/* Absences */}
          {(parseFloat(data.hours_sick || 0) > 0 || parseFloat(data.hours_leave || 0) > 0) && (
            <div style={{
              marginTop: '12px',
              padding: '10px 12px',
              background: '#fff3cd',
              borderRadius: '8px',
              fontSize: '12px',
              display: 'flex',
              gap: '16px',
              justifyContent: 'center'
            }}>
              {parseFloat(data.hours_sick || 0) > 0 && (
                <span>Maladie: {parseFloat(data.hours_sick).toFixed(0)}h</span>
              )}
              {parseFloat(data.hours_leave || 0) > 0 && (
                <span>Conges: {parseFloat(data.hours_leave).toFixed(0)}h</span>
              )}
            </div>
          )}

          {/* Sync button */}
          <button
            onClick={handleSync}
            disabled={syncing}
            style={{
              marginTop: '12px',
              width: '100%',
              padding: '8px',
              background: syncing ? '#6c757d' : '#e9ecef',
              color: '#495057',
              border: 'none',
              borderRadius: '6px',
              fontSize: '12px',
              cursor: syncing ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}
          >
            <span>{syncing ? '...' : '🔄'}</span>
            {syncing ? 'Synchronisation...' : 'Actualiser depuis les feuilles'}
          </button>

          {/* Derniere sync */}
          {data.last_sync_at && (
            <div style={{
              marginTop: '6px',
              fontSize: '10px',
              color: '#999',
              textAlign: 'center'
            }}>
              Derniere sync: {new Date(data.last_sync_at).toLocaleString('fr-FR')}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AnnualizationWidget;
